import { logComponentSelectorLoaded } from 'amplitude/helpers/componentSelector'
import { orgSelectors } from 'ducks/orgs'
import { permissionsSelectors } from 'ducks/permissions'
import { INVERTER_COMPATIBILITY_FILTER_NODE_KEY_V2 } from 'elements/hardwareFilter/inverterCompatibility/InverterCompatibilityFilterNodeFactoryV2'
import { SYSTEM_COMPATIBILITY_FILTER_NODE_KEY_V2 } from 'elements/hardwareFilter/systemCompatibility/SystemCompatibilityFilterNodeFactoryV2'
import type { FilterValuesType } from 'elements/hardwareFilter/type'
import { useDynamicFilterValues } from 'hooks/useDynamicFilterValues'
import { useOrgHidePricing } from 'hooks/useOrgHidePricing'
import { Button, FileQuestionIcon } from 'opensolar-ui'
import useEnabledHardwareSuppliers from 'pages/ordering/hooks/useEnabledHardwareSuppliers'
import { getComponentQuantityInSystem } from 'projectSections/designPanels/designRules/utils'
import { useComponentCost } from 'projectSections/hooks/useComponentCost'
import Notice from 'projectSections/sections/manage3/common/Notice'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslate } from 'react-admin'
import { useSelector } from 'react-redux'
import useDistributorTradePricing from 'services/useDistributorTradePricing'
import { StudioComponentItem } from 'types/global'
import { ComponentTypesV2 } from 'types/selectComponent'
import { RootState } from 'types/state'
import { useFeatureFlag } from 'util/featureFlags'
import { HARDWARE_FILTERS_DESIGN_PAGE_CONFIG_V2 } from '../hardwareFilter/constantsV2'
import { MY_LIST_FILTER_KEY } from '../hardwareFilter/viewFrom/constants'
import { isMultiSelectMode } from './isMultiSelectMode'
import { syncQuantityInSystem } from './syncQuantityInSystem'
import { applyPostFetchGuards } from './utils/postFetchGuards'

import HardwareSelectorFilterSidebar from './elements/HardwareSelectorFilterSidebar'
import { HardwareSelectorListPaginationFooter } from './elements/HardwareSelectorListPaginationFooter'
import HardwareSelectorHeader from './elements/HardwareSelectorHeader'
import HardwareSelectorLayout from './elements/HardwareSelectorLayout'
import HardwareSelectorList from './elements/HardwareSelectorList'
import HardwareSelectorToolBars, { getPlaceholderByComponentType } from './elements/HardwareSelectorToolBars'
import useHardwareSelectorFetchEngine from './fetch/useHardwareSelectorFetchEngine'
import useHandleSelect from './useHandleSelect'
import {
  useProjectHardwareSelectorFilterPersistence,
  useProjectHardwareSelectorPersistedFilterDefaults,
} from './useProjectHardwareSelectorFilterPreferences'

export type DistributorTradePricing = {
  [key: string]: boolean
}

export type DistributorTradePricingContextType = {
  distributorTradePricing: DistributorTradePricing
  isChecking: boolean
}

export const DistributorTradePricingContext = React.createContext<DistributorTradePricingContextType>({
  distributorTradePricing: {},
  isChecking: false,
})

export type HardwareSelectorDialogConfig = {
  componentTypes: ComponentTypesV2[]
  /** Set by withHardwareSelectorV2 when the dialog opens; guards stale loaded callbacks. */
  selectorSessionId?: number
  defaultFilterValues?: FilterValuesType
  targetUuids?: string[]
  slotKey?: string
  title?: string
  onSelect?: (addedObjects: StudioComponentItem[]) => void
  autoAdded?: boolean
  /** Opt-in: quantity UI + Done footer; dialog stays open until Done. Set from ComponentCollection bulk add only. */
  multiSelect?: boolean
  skipProjectPermissionCheck?: boolean
  /** When false, do not read/write per-project supplier filter prefs (e.g. system templates). */
  persistSupplierFilter?: boolean
  compatibilityData?: {
    compatibleBatteriesForInverters?: string[]
    compatibleInvertersForBatteries?: string[]
  }
}

export const HardwareSelectorCore = ({
  layoutOptions: { footer, isHeaderHidden } = {
    footer: null,
    isHeaderHidden: false,
  },
  onClose,
  config,
  onSelect,
}: {
  layoutOptions?: {
    footer?: React.ReactNode
    isHeaderHidden?: boolean
  }
  onClose(): void
  config: HardwareSelectorDialogConfig
  onSelect?: (addedObjects: StudioComponentItem[]) => void
}) => {
  const translate = useTranslate()
  const enabledMultiDistributorSelector = useFeatureFlag('design_multi_distributor', 'on')
  const persistSupplierFilter = config.persistSupplierFilter !== false
  const persistedFilterDefaults = useProjectHardwareSelectorPersistedFilterDefaults(persistSupplierFilter)

  const { filteredSuppliers: hardwareDistributors } = useEnabledHardwareSuppliers()
  const getOrgHidePricing = useOrgHidePricing()

  // Check if the distributor has trade pricing available. We use this to suppress
  // public pricing for some distributors.
  const { distributorTradePricing, isChecking } = useDistributorTradePricing({
    orgId: useSelector(orgSelectors.getOrg)?.id || 0,
    distributors: hardwareDistributors,
  })

  const dynamicFilterValues = useDynamicFilterValues()

  // `window.editor.selectedSystem` is not React state; derive a stable key so defaultFilterValues
  // recomputes when the user switches systems or battery totals change (same object can be mutated).
  const selectedSystem = window.editor?.selectedSystem
  const batteryPreviewSystemDepsKey = selectedSystem
    ? [
        selectedSystem.uuid,
        Math.round((selectedSystem.batteryTotalKwh?.() ?? 0) * 1000) / 1000,
        Math.round((selectedSystem.batteryTotalUsableKwh?.() ?? 0) * 1000) / 1000,
      ].join('|')
    : 'no-system'

  const defaultFilterValues = useMemo(() => {
    const systemBatteryKwhOptimal = selectedSystem?.batteryTotalKwh?.()
    const systemBatteryUsableKwh = selectedSystem?.batteryTotalUsableKwh?.()
    const batteryPreviewSystemValues =
      config.componentTypes.includes('battery')
        ? {
            // The hardware selector preview is component-scoped, so pass current design totals
            // explicitly when system-scoped battery placeholders need to render there.
            // If there is no selected system, fall back to 0 instead of leaving them blank.
            showcase_system_battery_kwh_optimal: Number.isFinite(systemBatteryKwhOptimal) ? systemBatteryKwhOptimal : 0,
            showcase_system_battery_usable_kwh: Number.isFinite(systemBatteryUsableKwh) ? systemBatteryUsableKwh : 0,
          }
        : {}

    return {
      ...dynamicFilterValues, // Dynamic values from getMatchedValueFromSpecsData
      ...batteryPreviewSystemValues,
      ...config.defaultFilterValues, // Static values from config
      ...persistedFilterDefaults,
    }
  }, [
    batteryPreviewSystemDepsKey,
    config.componentTypes,
    config.defaultFilterValues,
    dynamicFilterValues,
    persistedFilterDefaults,
  ])

  const { updateComponentCosts } = useComponentCost()

  const multiSelectMode = isMultiSelectMode(config) && Boolean(window.editor?.selectedSystem)
  const [selectedQuantities, setSelectedQuantities] = useState<Map<string, number>>(new Map())

  const handleSelect = useHandleSelect({ onClose, config, multiSelectMode })

  const handleQuantityChange = useCallback((component: { code: string; model_id: number }, newQuantity: number) => {
    const system = window.editor?.selectedSystem
    if (!system) return
    if (newQuantity === 0) {
      setSelectedQuantities((prev) => {
        const next = new Map(prev)
        next.delete(component.code)
        return next
      })
      syncQuantityInSystem(system, component.code, component.model_id, 0)
    } else {
      setSelectedQuantities((prev) => {
        const next = new Map(prev)
        next.set(component.code, newQuantity)
        return next
      })
      syncQuantityInSystem(system, component.code, component.model_id, newQuantity)
    }
  }, [])

  const doHandleSelect = useCallback(
    async (component, priceSource, price) => {
      if (onSelect) {
        onSelect(component)
      } else {
        await handleSelect(component)
      }

      if (enabledMultiDistributorSelector) {
        const componentActivation = window.AccountHelper.getComponentActivationFromCode(component.code, component.type)
        // This handles the default case for the pricing dropdown in the hardware selector
        const effectivePrice = priceSource === 'component' ? componentActivation?.cost || 0 : price

        /// Only set a value for the price source if it is selected. If it is not selected,
        /// the usePreference hook should pick that up and set it there.
        if (priceSource) {
          updateComponentCosts([
            {
              code: component.code,
              source: priceSource,
              price: effectivePrice,
              activationId: componentActivation?.id || 0,
            },
          ])
        }
      }
    },
    [handleSelect, onSelect, enabledMultiDistributorSelector, updateComponentCosts]
  )

  const listOnSelect = useCallback(
    async (component: any, priceSource?: any, price?: number) => {
      await doHandleSelect(component, priceSource, price)
      if (multiSelectMode) {
        setSelectedQuantities((prev) => {
          const next = new Map(prev)
          const system = window.editor?.selectedSystem
          const current = system
            ? getComponentQuantityInSystem(system, 'other', component.code)
            : prev.get(component.code) ?? 0
          next.set(component.code, current)
          return next
        })
      }
    },
    [doHandleSelect, multiSelectMode]
  )

  const {
    componentsData,
    setFilters,
    total = 0,
    loading,
    loaded,
    page,
    perPage,
    setPage,
    setPerPage,
    persistentFilterValues,
    modifiableFilterValues,
    allFilterValues,
    setBlockRequest,
  } = useHardwareSelectorFetchEngine({
    componentTypes: config.componentTypes,
    limit: 20,
    defaultFilterValues,
    blockRequestInitially: true,
  })

  const { handleFiltersReady } = useProjectHardwareSelectorFilterPersistence({
    enabled: persistSupplierFilter,
    modifiableFilterValues,
    setBlockRequest,
  })

  const filteredComponentsData: any[] = useMemo(() => {
    return applyPostFetchGuards({
      componentsData,
      componentTypes: config.componentTypes,
      allFilterValues,
      slotKey: config.slotKey,
      targetUuids: config.targetUuids,
    })
  }, [componentsData, allFilterValues, config.componentTypes, config.slotKey, config.targetUuids])

  const componentTypesKey = config.componentTypes.join(',')
  const selectorSessionId = config.selectorSessionId

  useEffect(() => {
    if (!loaded || loading || selectorSessionId == null) return
    logComponentSelectorLoaded(config.componentTypes, selectorSessionId)
  }, [loaded, loading, componentTypesKey, selectorSessionId])

  useEffect(() => {
    if (!multiSelectMode || !componentsData?.length) return
    const system = window.editor?.selectedSystem
    if (!system) return
    setSelectedQuantities((prev) => {
      const next = new Map(prev)
      for (const c of componentsData) {
        const code = c.code
        if (!next.has(code)) {
          next.set(code, getComponentQuantityInSystem(system, 'other', code))
        }
      }
      return next
    })
  }, [multiSelectMode, componentsData])

  const sharedFromOrgId = modifiableFilterValues?.shared_from
  const hidePricing = sharedFromOrgId
    ? getOrgHidePricing(sharedFromOrgId) // If viewing shared components, check org-specific hide pricing setting
    : false // Otherwise show pricing

  const handleUpdate = useCallback(
    (newFilterValues: { [key: string]: unknown }, immediate = true) => {
      setFilters({ ...modifiableFilterValues, ...newFilterValues }, undefined, immediate)
    },
    [modifiableFilterValues]
  )

  const handleSearchTextUpdate = useCallback(
    (searchText) => {
      // Debounce search to avoid firing a request on every keystroke.
      handleUpdate({ search: searchText }, false)
    },
    [handleUpdate]
  )

  const projectPermissionsLoaded = useSelector(permissionsSelectors.getProjectPermissionsLoaded)
  const projectPermission = useSelector((state: RootState) =>
    permissionsSelectors.getProjectPermissionByKey(state, 'components', true)
  )
  const rolePermission = useSelector((state: RootState) => permissionsSelectors.getPermissionByKey(state, 'components'))
  const allowView = projectPermissionsLoaded ? projectPermission.allowView : rolePermission.allowView

  const hiddenColumnGroups = new Set<string>()

  if (hidePricing) {
    hiddenColumnGroups.add('price_stock_distributor')
  }

  const shouldShowRating = config.componentTypes.some((type) => ['module', 'inverter', 'battery'].includes(type))
  if (!shouldShowRating) {
    hiddenColumnGroups.add('rating')
  }

  const isMyListSelected = Boolean(allFilterValues?.[MY_LIST_FILTER_KEY])

  const doneFooter = multiSelectMode ? (
    <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '16px 0' }}>
      <Button variant="contained" color="primary" onClick={onClose}>
        {translate('Done')}
      </Button>
    </div>
  ) : null

  return (
    <DistributorTradePricingContext.Provider value={{ distributorTradePricing, isChecking }}>
      <HardwareSelectorLayout
        footer={footer ?? doneFooter}
        heading={
          !isHeaderHidden ? (
            <HardwareSelectorHeader title={config.title || translate('Select Hardware')} handleClose={onClose} />
          ) : null
        }
        toolBars={
          <HardwareSelectorToolBars
            componentType={config.componentTypes[0]}
            placeholder={getPlaceholderByComponentType(config.componentTypes[0], translate)}
            onChange={handleSearchTextUpdate}
          />
        }
        listContent={
          <HardwareSelectorList
            total={total}
            loading={loading}
            data={filteredComponentsData}
            onSelect={multiSelectMode ? listOnSelect : doHandleSelect}
            Pagination={
              <HardwareSelectorListPaginationFooter
                total={total}
                page={page}
                perPage={perPage}
                setPage={setPage}
                setPerPage={setPerPage}
              />
            }
            hiddenColumnGroups={hiddenColumnGroups}
            skipProjectPermissionCheck={config.skipProjectPermissionCheck}
            multiSelectMode={multiSelectMode}
            selectedQuantities={multiSelectMode ? selectedQuantities : undefined}
            onQuantityChange={multiSelectMode ? handleQuantityChange : undefined}
            noResults={
              <Notice
                icon={<FileQuestionIcon width={'20px'} height={'20px'} />}
                mainText={
                  allFilterValues?.shared_from
                    ? translate('No components have been shared from the connected organisation.')
                    : translate('No results found.')
                }
                subText={
                  allFilterValues?.shared_from
                    ? translate('Try selecting "All" or "Showcase Partners" to browse other components.')
                    : isMyListSelected
                    ? translate('Select view items from "All" to see results outside of "My List".')
                    : translate('Try adjusting your search or changing filters to explore more.')
                }
              />
            }
          />
        }
        sidebar={
          <HardwareSelectorFilterSidebar
            hardwareFilterConfigList={useMemo(() => {
              if (!config.compatibilityData) {
                return HARDWARE_FILTERS_DESIGN_PAGE_CONFIG_V2
              }

              // There are circumstances like the AUto Design Template CRUD form where we want to support compatibility
              // Checks in a context which studio is not loaded. This means we can't rely on editor.selectedSystem
              // for compatibility data and instead use /component_compatibility/ endpoint to get the data and
              // pass it in as a prop.
              const { compatibleBatteriesForInverters, compatibleInvertersForBatteries } = config.compatibilityData

              return HARDWARE_FILTERS_DESIGN_PAGE_CONFIG_V2.map((filterConfig) => {
                switch (filterConfig.key) {
                  case SYSTEM_COMPATIBILITY_FILTER_NODE_KEY_V2:
                    return { ...filterConfig, compatibleCodes: compatibleBatteriesForInverters || [] }
                  case INVERTER_COMPATIBILITY_FILTER_NODE_KEY_V2:
                    return { ...filterConfig, compatibleCodes: compatibleInvertersForBatteries || [] }
                  default:
                    return filterConfig
                }
              })
            }, [config.compatibilityData])}
            persistentFilterValues={persistentFilterValues}
            modifiableFilterValues={modifiableFilterValues}
            allFilterValues={allFilterValues}
            setModifiableFilterValues={setFilters}
            componentTypes={config.componentTypes}
            location="design"
            onFiltersReady={handleFiltersReady}
          />
        }
      />
    </DistributorTradePricingContext.Provider>
  )
}

export default HardwareSelectorCore
